import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Input, Tooltip, message,Spin } from "antd";
import {
  MailOutlined,
  LockOutlined,
  ShopOutlined,
  CameraOutlined,
  CheckCircleFilled,
  SafetyOutlined,
} from "@ant-design/icons";
import Gravatar from "react-gravatar";
import get from "lodash/get";
import find from "lodash/find";
import map from "lodash/map";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";

import requireAuth from "../../hocs/requireAuth";
import { changePassword } from "../../redux/actions/authActions";
import { fetchCompanies } from "../../redux/actions/companyActions";
import { CHANGE_PASSWORD_SUCCESS } from "../../constants/message";
import { USER_ROLES } from "../../constants/userRoles";
import { COLOR_PRIMARY } from "../../constants";

import { PASSWORD_RULES, GRAVATAR_URL, STRENGTH_LEVELS } from '../../constants/profile';

const getRoleConfig = (role) =>
  find(USER_ROLES, (roleObj) => includes(get(roleObj, "ids"), get(role, "id")));

const SectionCard = ({ icon, title, subtitle, extra, children }) => (
  <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-7">
    <div className="flex items-start justify-between gap-4 mb-5">
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${COLOR_PRIMARY}14`, color: COLOR_PRIMARY }}
        >
          {icon}
        </div>
        <div>
          <p className="text-base font-semibold text-black-2 mb-0">{title}</p>
          {subtitle && <p className="text-xs text-grey-3 mb-0 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {extra}
    </div>
    {children}
  </div>
);

const CompanySection = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([])

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        setCompanies(result);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies();
  }, [])

  return (
    <SectionCard
      icon={<ShopOutlined />}
      title="Company Access"
      subtitle="Companies you can view reports for"
      extra={
        <span className="text-xs font-semibold text-grey-3 bg-grey-2 rounded-full px-3 py-1 shrink-0">
          {get(companies, "length", 0)}
        </span>
      }
    >
      <Spin spinning={loading}>
        {isEmpty(companies) ? (
          <div className="border border-dashed border-grey-1 rounded-xl py-8 text-center">
            <p className="text-sm text-grey-3 mb-0">No companies assigned yet</p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2.5">
            {map(companies, (company) => (
              <span
                key={get(company, "id")}
                className="flex items-center gap-2 text-sm text-black-1 bg-grey-2 border border-grey-1 rounded-xl px-3.5 py-2 transition hover:border-primary hover:text-primary"
              >
                <ShopOutlined className="text-grey-3" />
                {get(company, "name")}
              </span>
            ))}
          </div>
        )}
      </Spin>
    </SectionCard>
  )
}

const ProfileScreen = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  
  const [form] = Form.useForm();

  const user = useSelector((state) => get(state, "auth.user"));
  const companies = useSelector((state) =>
    get(state, "location.userZoneLocations.companyList", [])
  );

  const newPassword = Form.useWatch("newPassword", form) || "";
  const roleConfig = getRoleConfig(get(user, "role"));
  const roleLabel = get(roleConfig, "label") || get(user, "role.name") || "—";
  const roleColor = get(roleConfig, "avatarColor") || COLOR_PRIMARY;

  const passedRules = PASSWORD_RULES.filter((rule) => rule.test(newPassword));
  const strength = get(STRENGTH_LEVELS, [Math.max(passedRules.length - 1, 0)]);


  const onFinish = (values) => {
    setLoading(true);
    dispatch(
      changePassword({
        oldPassword: get(values, "oldPassword"),
        newPassword: get(values, "newPassword"),
        confirmPassword:get(values, "confirmPassword")
      })
    )
      .then(() => {
        setLoading(false);
        message.success(CHANGE_PASSWORD_SUCCESS);
        form.resetFields();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return (
    <div className="pl-2 py-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <p className="text-xl font-medium leading-[30px] text-black-1">My Profile</p>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hidden pb-5">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
          <div className="flex flex-col gap-6">
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div
                className="h-20"
                style={{
                  backgroundImage: `linear-gradient(120deg, ${COLOR_PRIMARY} 0%, #6C7CF5 55%, #8E7BF8 100%)`,
                }}
              />
              <div className="px-6 sm:px-7 pb-6 -mt-10">
                <Tooltip title="Change photo on Gravatar">
                  <a
                    href={GRAVATAR_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="group relative inline-block rounded-2xl overflow-hidden ring-4 ring-white shadow-md"
                  >
                    <Gravatar email={get(user, "email")} size={80} default="identicon" className="block bg-white" />
                    <span className="absolute inset-0 bg-black-2/50 text-white flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                      <CameraOutlined />
                    </span>
                  </a>
                </Tooltip>

                <div className="flex items-center flex-wrap gap-3 mt-3">
                  <p className="text-xl font-semibold text-black-2 mb-0 truncate">
                    {get(user, "name")}
                  </p>
                  <span
                    className="text-xs font-semibold px-3 py-1 rounded-full"
                    style={{ color: roleColor, backgroundColor: `${roleColor}1F` }}
                  >
                    {roleLabel}
                  </span>
                </div>

                <button
                  type="button"
                  className="group flex items-center gap-2 text-sm text-grey-3 mt-1.5 transition hover:text-primary"
                >
                  <MailOutlined />
                  <span className="truncate">{get(user, "email")}</span>
                </button>
              </div>
            </div>

            <CompanySection companies={companies} />
          </div>

          <SectionCard
            icon={<SafetyOutlined />}
            title="Change Password"
            subtitle="Use a strong password you don't reuse elsewhere"
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              onFinishFailed={() => {}}
              requiredMark={false}
              autoComplete="off"
            >
              <Form.Item
                label="Current Password"
                name="oldPassword"
                rules={[{ required: true, message: "Please input your current password...!" }]}
              >
                <Input.Password
                  size="large"
                  placeholder="Current Password"
                  prefix={<LockOutlined className="text-grey-3 mr-1" />}
                />
              </Form.Item>

              <Form.Item
                label="New Password"
                name="newPassword"
                className="mb-2"
                rules={[
                  { required: true, message: "Please input your new password...!" },
                  { min: 8, message: "Password must be at least 8 characters...!" },
                ]}
              >
                <Input.Password
                  size="large"
                  placeholder="New Password"
                  prefix={<LockOutlined className="text-grey-3 mr-1" />}
                />
              </Form.Item>

              <div className="mb-5">
                <div className="flex items-center gap-1.5 mb-3">
                  {map(STRENGTH_LEVELS, (level, index) => (
                    <span
                      key={get(level, "label")}
                      className="h-1.5 flex-1 rounded-full transition-colors"
                      style={{
                        backgroundColor:
                          newPassword && index < passedRules.length
                            ? get(strength, "color")
                            : "#DEE4ED",
                      }}
                    />
                  ))}
                  <span
                    className="text-xs font-medium w-12 text-right"
                    style={{ color: newPassword ? get(strength, "color") : "#8B8D97" }}
                  >
                    {newPassword ? get(strength, "label") : ""}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-3">
                  {map(PASSWORD_RULES, (rule) => {
                    const passed = rule.test(newPassword);
                    return (
                      <div key={get(rule, "key")} className="flex items-center gap-2">
                        <CheckCircleFilled
                          className="text-xs"
                          style={{ color: passed ? "#52C41A" : "#DEE4ED" }}
                        />
                        <span className={`text-xs ${passed ? "text-black-1" : "text-grey-3"}`}>
                          {get(rule, "label")}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Form.Item
                label="Confirm New Password"
                name="confirmPassword"
                dependencies={["newPassword"]}
                hasFeedback
                rules={[
                  { required: true, message: "Please confirm your new password...!" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("newPassword") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Passwords do not match...!"));
                    },
                  }),
                ]}
              >
                <Input.Password
                  size="large"
                  placeholder="Confirm New Password"
                  prefix={<LockOutlined className="text-grey-3 mr-1" />}
                />
              </Form.Item>

              <Form.Item label={null} className="flex justify-end mb-0">
                <Button type="primary" size="large" htmlType="submit" loading={loading}>
                  Update Password
                </Button>
              </Form.Item>
            </Form>
          </SectionCard>
        </div>
      </div>
    </div>
  );
};

export default requireAuth(ProfileScreen);
